import styled, { keyframes } from "styled-components";
import { INK, AXIS, GRID } from "../../../ChartUtility/recharts";

const INK_3 = "#64748b";
const SURFACE = "#ffffff";
const TRACK = "#f1f5f9";

export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const Hero = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px 22px 20px;
  background: ${SURFACE};
  border: 1px solid ${GRID};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

export const HeroTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 28px;
`;

export const HeroTotal = styled.div`
  flex-shrink: 0;
`;

export const HeroLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${INK_3};
  margin-bottom: 6px;
`;

export const HeroValue = styled.div`
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: ${INK};
  font-variant-numeric: tabular-nums;

  span {
    font-size: 14px;
    font-weight: 600;
    color: ${AXIS};
    letter-spacing: 0.02em;
  }
`;

export const HeroCats = styled.div`
  flex: 1;
  min-width: 260px;
`;

export const HeroBar = styled.div`
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: ${TRACK};
`;

export const HeroBarNote = styled.div`
  margin-top: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${AXIS};
`;

export const BarSeg = styled.span`
  height: 100%;
  transition: width 0.5s ease;
  width: ${(p) => p.$width}%;
  background: ${(p) => p.$color};
`;

export const Dot = styled.span`
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  vertical-align: middle;
  background: ${(p) => p.$color};
`;

export const Grid = styled.div`
  display: grid;
  /* $single: the side panel is omitted, so the chart takes the full width. */
  grid-template-columns: ${(p) => (p.$single ? "1fr" : "1.6fr 1fr")};
  gap: 16px;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.section`
  background: ${SURFACE};
  border: 1px solid ${GRID};
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  min-width: 0;
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

export const PanelTitles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const PanelTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
`;

export const PanelSub = styled.span`
  font-size: 11px;
  color: ${AXIS};
`;

export const Toggle = styled.div`
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  background: ${TRACK};
  border-radius: 8px;
`;

export const ToggleBtn = styled.button`
  border: none;
  background: ${(p) => (p.$active ? SURFACE : "transparent")};
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => (p.$active ? INK : INK_3)};
  border-radius: 6px;
  cursor: pointer;
  box-shadow: ${(p) => (p.$active ? "0 1px 2px rgba(0, 0, 0, 0.08)" : "none")};
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    color: ${INK};
  }
  &:focus-visible {
    outline: 2px solid ${AXIS};
    outline-offset: 1px;
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
`;

export const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 220px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => (p.$on ? p.$color : INK_3)};
  background: ${(p) => (p.$on ? `${p.$color}14` : SURFACE)};
  border: 1px solid ${(p) => (p.$on ? p.$color : GRID)};
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${(p) => (p.$on ? p.$color : "#cbd5e1")};
    background: ${(p) => (p.$on ? `${p.$color}22` : "#f8fafc")};
  }
  &:focus-visible {
    outline: 2px solid ${AXIS};
    outline-offset: 1px;
  }
`;

export const ChipDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 1.5px solid ${(p) => p.$color};
  background: ${(p) => (p.$on ? p.$color : "transparent")};
  flex-shrink: 0;
  box-sizing: border-box;
`;

export const Note = styled.div`
  margin-top: 8px;
  font-size: 11px;
  font-style: italic;
  color: ${AXIS};
`;

export const TopKpis = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const TopKpiHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 5px;
`;

export const TopKpiName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const TopKpiPct = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${INK_3};
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

export const TopKpiTrack = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${TRACK};
  overflow: hidden;
`;

export const TopKpiFill = styled.div`
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
  width: ${(p) => Math.min(p.$pct, 100)}%;
  background: ${(p) => p.$color};
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
`;

export const StatCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 13px 15px 14px 17px;
  background: #fcfdfe;
  border: 1px solid ${GRID};
  border-radius: 10px;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 3px;
    background: ${(p) => p.$accent || GRID};
  }
`;

export const StatLabel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 7px;
  min-height: 28px;
  margin-bottom: 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${INK_3};
`;

export const StatSwatch = styled.span`
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  margin-top: 3px;
  border-radius: 3px;
  background: ${(p) => p.$color};
`;

export const StatValue = styled.div`
  font-size: ${(p) => (p.$lg ? 30 : 26)}px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${(p) => p.$tone || INK};
  font-variant-numeric: tabular-nums;

  span {
    margin-left: 5px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: ${AXIS};
  }
`;

export const StatShare = styled.div`
  height: 4px;
  margin-top: 11px;
  border-radius: 999px;
  background: ${TRACK};
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    border-radius: 999px;
    transition: width 0.5s ease;
    width: ${(p) => Math.min(Math.max(p.$pct, 0), 100)}%;
    background: ${(p) => p.$color};
  }
`;

// margin-top:auto pins the footer to the card base so all three cards line up,
// even though the Difference card has no share bar.
export const StatFoot = styled.div`
  margin-top: auto;
  padding-top: 7px;
  font-size: 11px;
  line-height: 1.55;
  color: ${AXIS};
  font-variant-numeric: tabular-nums;

  strong {
    font-weight: 700;
    color: ${INK_3};
  }
`;

const shimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`;

export const Skeleton = styled.div`
  height: 320px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e9eef4 37%, #f1f5f9 63%);
  background-size: 400% 100%;
  animation: ${shimmer} 1.4s ease infinite;
`;

export const Empty = styled.div`
  font-size: 13px;
  color: ${AXIS};
  padding: 24px 0;
  text-align: center;
`;
